import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as AfricaMap } from "../assets/redesign/map/africa.svg";
import { ReactComponent as AmericaCentralMap } from "../assets/redesign/map/america-central.svg";
import { ReactComponent as AmericaNorthMap } from "../assets/redesign/map/america-north.svg";
import { ReactComponent as AmericaSouthMap } from "../assets/redesign/map/america-south.svg";
import { ReactComponent as AsiaMap } from "../assets/redesign/map/asia.svg";
import { ReactComponent as AustraliaMap } from "../assets/redesign/map/australia.svg";
import { ReactComponent as EuropeMap } from "../assets/redesign/map/europe.svg";
import { ReactComponent as JapanMap } from "../assets/redesign/map/japan.svg";
import { ReactComponent as KoreaMap } from "../assets/redesign/map/korea.svg";
import { ReactComponent as UkMap } from "../assets/redesign/map/uk.svg";
import apiInstance from "../../../shared/utils/axios";
import "../styles/HomeRedesign.css";

const mapParts = [
  {
    className: "america-north",
    Component: AmericaNorthMap,
    continentCode: "AMERICA",
  },
  {
    className: "america-central",
    Component: AmericaCentralMap,
    continentCode: "AMERICA",
  },
  {
    className: "america-south",
    Component: AmericaSouthMap,
    continentCode: "AMERICA",
  },
  { className: "uk", Component: UkMap },
  { className: "africa", Component: AfricaMap },
  { className: "asia", Component: AsiaMap, continentCode: "ASIA" },
  { className: "japan", Component: JapanMap },
  { className: "korea", Component: KoreaMap },
  { className: "europe", Component: EuropeMap, continentCode: "EUROPE" },
  { className: "australia", Component: AustraliaMap, continentCode: "OCEANIA" },
];

const cities = [
  "Seoul",
  "Tokyo",
  "Berlin",
  "Paris",
  "New York",
  "London",
  "Avignon",
  "São Paulo",
  "Melbourne",
  "Edinburgh",
  "Chicago",
  "Amsterdam",
  "Sydney",
  "Zürich",
  "Hamburg",
  "Copenhagen",
  "Singapore",
];

type LocationStats = {
  artists: string;
  venues: string;
  shows: string;
};

type MapLocation = {
  code: string;
  country: string;
  pin: [number, number];
  tooltip: [number, number];
};

type RegionStat = {
  type: "CONTINENT" | "COUNTRY";
  code: string;
  name: string;
  nameKo: string;
  artists: number;
  venues: number;
  shows: number;
};

type RegionStatsResponse = {
  status: number;
  message: string;
  data: {
    continents: RegionStat[];
    countries: RegionStat[];
  };
};

type RegionStatsData = RegionStatsResponse["data"];

// 국가 코드별 지도 포인트 및 툴팁 위치
const mapLocations: MapLocation[] = [
  { code: "US", country: "USA", pin: [12.92, 30], tooltip: [16, 28] },
  { code: "GB", country: "UK", pin: [44.15, 20.49], tooltip: [44, 12] },
  { code: "KR", country: "Korea", pin: [91.04, 30.98], tooltip: [64, 30] },
  { code: "AU", country: "Australia", pin: [92.53, 74.39], tooltip: [64, 48] },
  { code: "DE", country: "German", pin: [46.39, 22.44], tooltip: [46, 19.5] },
];

const mapRegions = [
  { code: "AMERICA", name: "America", tooltip: [15, 18] as [number, number] },
  { code: "EUROPE", name: "Europe", tooltip: [46, 19.5] as [number, number] },
  { code: "ASIA", name: "Asia", tooltip: [62, 16] as [number, number] },
  { code: "OCEANIA", name: "Oceania", tooltip: [64, 48] as [number, number] },
];

const emptyStats: LocationStats = { artists: "0", venues: "0", shows: "0" };

function formatStats(stat?: RegionStat): LocationStats {
  if (!stat) return emptyStats;

  return {
    artists: stat.artists.toLocaleString("en-US"),
    venues: stat.venues.toLocaleString("en-US"),
    shows: stat.shows.toLocaleString("en-US"),
  };
}

function TooltipStats({ stats }: { stats: LocationStats }) {
  return (
    <>
      <span>
        <b>{stats.artists}</b>ARTISTS
      </span>
      <span>
        <b>{stats.venues}</b>VENUES
      </span>
      <span>
        <b>{stats.shows}</b>SHOWS
      </span>
    </>
  );
}

export default function GlobalStageSection() {
  const [regionStats, setRegionStats] = useState<RegionStatsData>({
    continents: [],
    countries: [],
  });
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [lastKey, setLastKey] = useState("region:EUROPE");
  const tooltipContents = useMemo(() => {
    const continentsByCode = new Map(
      regionStats.continents.map((stat) => [stat.code, stat]),
    );
    const countriesByCode = new Map(
      regionStats.countries.map((stat) => [stat.code, stat]),
    );

    return [
      ...mapRegions.map((region) => {
        const stat = continentsByCode.get(region.code);
        return {
          key: `region:${region.code}`,
          label: "REGION",
          name: stat?.name || region.name,
          tooltip: region.tooltip,
          stats: formatStats(stat),
        };
      }),
      ...mapLocations.map((location) => {
        const stat = countriesByCode.get(location.code);
        return {
          key: `point:${location.code}`,
          label: "COUNTRY",
          name: stat?.name || location.country,
          tooltip: location.tooltip,
          stats: formatStats(stat),
        };
      }),
    ];
  }, [regionStats]);
  const activeTooltip = tooltipContents.find(({ key }) => key === activeKey);
  const displayedTooltip =
    activeTooltip ?? tooltipContents.find(({ key }) => key === lastKey)!;
  const tooltipStyle = {
    "--tooltip-left": `${displayedTooltip.tooltip[0]}%`,
    "--tooltip-top": `${displayedTooltip.tooltip[1]}%`,
  } as React.CSSProperties;
  const openTooltip = (key: string) => {
    setLastKey(key);
    setActiveKey(key);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchRegionStats = async () => {
      try {
        const response = await apiInstance.get("/api/region/stats");
        const result = response.data as RegionStatsResponse;

        if (!cancelled && result.status === 200 && result.data)
          setRegionStats(result.data);
      } catch {
        // 조회 실패 시 지도 인터랙션과 0값 표시 유지
      }
    };

    fetchRegionStats();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeKey) return;

    const closeTooltipOutside = (event: PointerEvent) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        !target.closest(
          ".global-stage__region-trigger, .map-location-trigger, .map-tooltip",
        )
      )
        setActiveKey(null);
    };
    const closeTooltipWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveKey(null);
    };

    document.addEventListener("pointerdown", closeTooltipOutside);
    document.addEventListener("keydown", closeTooltipWithEscape);
    return () => {
      document.removeEventListener("pointerdown", closeTooltipOutside);
      document.removeEventListener("keydown", closeTooltipWithEscape);
    };
  }, [activeKey]);

  return (
    <section className="global-stage">
      <div className="global-stage__copy">
        <h1 className="global-stage__title">
          <span>
            <b>Here,</b>
            <span>the stage</span>
          </span>
          <span>
            <b>We,</b>
            <i>the creators</i>
          </span>
          <span>
            <b>Are,</b>
            <span>everywhere</span>
          </span>
        </h1>
        <div className="global-stage__description">
          <p>
            HereWeAre&nbsp;is the worldwide directory and lounge for performing
            artists -&nbsp;<em>designed by creatives, for creatives.</em>
          </p>
          <p>
            We are on a mission
            <strong> to map, connect, and revolutionize our&nbsp;industry.</strong>
          </p>
        </div>
      </div>

      <div className="global-stage__map">
        {mapParts.map(({ className, Component, continentCode }) => {
          const regionKey = continentCode
            ? `region:${continentCode}`
            : undefined;
          return (
            <button
              key={className}
              type="button"
              className={`global-stage__map-part global-stage__map-part--${className}${
                regionKey ? " global-stage__region-trigger" : ""
              }${activeKey === regionKey ? " is-active" : ""}`}
              aria-label={
                continentCode
                  ? `${
                      mapRegions.find(({ code }) => code === continentCode)
                        ?.name
                    } 대륙 정보`
                  : undefined
              }
              aria-expanded={regionKey ? activeKey === regionKey : undefined}
              tabIndex={regionKey ? 0 : -1}
              onMouseEnter={() => regionKey && openTooltip(regionKey)}
              onMouseLeave={() => regionKey && setActiveKey(null)}
              onFocus={() => regionKey && openTooltip(regionKey)}
              onBlur={() => regionKey && setActiveKey(null)}
              onClick={() => regionKey && openTooltip(regionKey)}
            >
              <Component />
            </button>
          );
        })}
        {mapLocations.map((location) => {
          const pinStyle = {
            "--pin-left": `${location.pin[0]}%`,
            "--pin-top": `${location.pin[1]}%`,
          } as React.CSSProperties;

          return (
            <button
              key={location.code}
              type="button"
              className={`map-location-trigger map-location-trigger--${location.code.toLowerCase()}${
                activeKey === `point:${location.code}` ? " is-active" : ""
              }`}
              style={pinStyle}
              aria-label={`${location.country} 국가 정보`}
              aria-expanded={activeKey === `point:${location.code}`}
              onMouseEnter={() => openTooltip(`point:${location.code}`)}
              onMouseLeave={() => setActiveKey(null)}
              onFocus={() => openTooltip(`point:${location.code}`)}
              onBlur={() => setActiveKey(null)}
              onClick={() => openTooltip(`point:${location.code}`)}
            >
              <span className="map-location-trigger__pin" />
            </button>
          );
        })}
        <div
          className={`map-tooltip${activeTooltip ? " is-visible" : ""}`}
          style={tooltipStyle}
          role="tooltip"
        >
          <div className="map-tooltip__title">
            <span>{displayedTooltip.label}</span>
            <strong>{displayedTooltip.name}</strong>
          </div>
          <div className="map-tooltip__stats">
            <TooltipStats stats={displayedTooltip.stats} />
          </div>
        </div>
      </div>

      <div className="city-marquee" aria-label={cities.join(", ")}>
        <div className="city-marquee__track">
          {[0, 1].map((groupIndex) => (
            <div
              className="city-marquee__group"
              key={groupIndex}
              aria-hidden={groupIndex === 1}
            >
              {cities.map((city, index) => (
                <React.Fragment key={city}>
                  <span
                    className={`city-marquee__city city-marquee__city--${
                      index % 3
                    }`}
                  >
                    {city}
                  </span>
                  <span className="city-marquee__dot" />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
